import { MatSelectOption } from './../../../_models/mat-select-option';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { getISODayInWarsaw, isSameDayInWarsaw, RESTAURANT_TIMEZONE, toWarsawSlot } from './warsaw-timezone.helper';
import { formatInTimeZone } from 'date-fns-tz';
import { DwResponse } from './../../../_models/dw-response';
import { Table } from './../../../_models/table';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from './../../../environments/environment';
import { Injectable } from '@angular/core';
import { ReservationTables } from './../../../_models/reservation-tables';
import { OpeningHour } from './../../../_models/opening-hour';
import {
  ReservationWithTables,
  ReservationWithTablesRequest,
} from './../../../_models/reservation';
import { TableAvailability } from '../../../_models/table-availability';
import { DWDateRange } from '../../../_models/dw-date-range';
import { TableStatuses } from '../../../_enums/table-statuses.enum';
import { ReservationStatus } from '../../../_enums/reservation-status.enum';
import { SpecialDay } from '../../../_models/special_day';

/**
 * Minimalna przerwa (w minutach) wymagana pomiędzy rezerwacjami na tym samym
 * stoliku. Wymaganie kelnerów: po rezerwacji 18:00–20:00 kolejna rezerwacja
 * tego stolika może rozpocząć się najwcześniej o 20:30.
 *
 * Bufor obowiązuje po KAŻDEJ rezerwacji, więc okno rezerwacji rozważanej
 * (2h / 1.5h) też wydłużamy o bufor — inaczej dałoby się ją „dokleić" tuż
 * przed rezerwacją już istniejącą.
 */
const RESERVATION_GAP_MINUTES = 30;

@Injectable({
  providedIn: 'root',
})
export class ReservationsService {
  reservationTablesUrl = environment.apiUrl + '/reservation_tables';
  reservationsUrl = environment.apiUrl + '/reservations';
  tablesUrl = environment.apiUrl + '/tables';

  private choosenTables: Table[] = [];
  choosenTablesSub: BehaviorSubject<Table[]> = new BehaviorSubject<Table[]>(
    this.choosenTables
  );
  $choosenTables: Observable<Table[]> = this.choosenTablesSub.asObservable();

  private notAvailableTables: TableAvailability[] = [];
  notAvailableTablesSub: BehaviorSubject<TableAvailability[]> =
    new BehaviorSubject<TableAvailability[]>(this.notAvailableTables);
  $notAvailableTables: Observable<TableAvailability[]> =
    this.notAvailableTablesSub.asObservable();

  private parkingTicketNeededTables: Table[] = [];
  parkingTicketNeededTablesSub: BehaviorSubject<Table[]> = new BehaviorSubject<
    Table[]
  >(this.parkingTicketNeededTables);
  $parkingTicketNeededTables: Observable<Table[]> =
    this.parkingTicketNeededTablesSub.asObservable();

  tableClickedSub: Subject<Table> = new Subject();

  constructor(private http: HttpClient) {}

  tableClicked(table: Table): void {
    if (
      this.notAvailableTables.find(
        (notAvailableTable) => notAvailableTable.tableId === table.id
      )
    ) {
    } else {
      if (this.choosenTables.includes(table)) {
        this.choosenTables = this.choosenTables.filter(
          (choosenTable) => choosenTable !== table
        );
      } else {
        this.choosenTables.push(table);
      }
      this.choosenTablesSub.next(this.choosenTables);
    }

    this.tableClickedSub.next(table);
  }

  unselectAllTables(): void {
    this.choosenTables = [];
    this.choosenTablesSub.next(this.choosenTables);
  }

  calculateNotAvailableTablesWithParking(
    reservationTables: ReservationWithTables[],
    startDate: Date
  ): TableAvailability[] {
    let parkingNeededTables: TableAvailability[] = [];
    // Okna rozważanej rezerwacji wydłużone o bufor po jej zakończeniu.
    let twoHoursDateRange: DWDateRange = {
      start: startDate,
      end: addMinutes(addHours(startDate, 2), RESERVATION_GAP_MINUTES),
    };

    let oneHourHalfDateRange: DWDateRange = {
      start: startDate,
      end: addMinutes(startDate, 90 + RESERVATION_GAP_MINUTES),
    };

    let occupiedDateRange: DWDateRange = {
      start: startDate,
      end: startDate,
    };
    parkingNeededTables = reservationTables
      .filter(
        (reservationTable) =>
          reservationTable.status === ReservationStatus.CREATED ||
          reservationTable.status === ReservationStatus.GUEST_ARRIVED
      )
      .reduce((prev, curr) => {
        // Rozszerzamy okno zajętości stolika o bufor – kolejna rezerwacja tego
        // samego stolika może rozpocząć się najwcześniej RESERVATION_GAP_MINUTES
        // po zakończeniu poprzedniej.
        let secondDateRange: DWDateRange = {
          start: curr.startDate,
          end: addMinutes(curr.endDate, RESERVATION_GAP_MINUTES),
        };
        if (
          this.checkIfDateRangesIntersects(occupiedDateRange, secondDateRange)
        ) {
          for (let table of curr.tables)
            prev.push({
              tableId: table.id,
              startDate: curr.startDate,
              status: TableStatuses.OCCUPIED,
              parkingNeeded: curr.vehiclesNumber > 0 && !curr.parkingCodeDelivered,
            });
        } else if (
          this.checkIfDateRangesIntersects(oneHourHalfDateRange, secondDateRange)
        ) {
          for (let table of curr.tables)
            prev.push({
              tableId: table.id,
              startDate: curr.startDate,
              status: TableStatuses.ONE_HOUR_HALF,
              parkingNeeded: curr.vehiclesNumber > 0 && !curr.parkingCodeDelivered,
            });
        } else if (
          this.checkIfDateRangesIntersects(twoHoursDateRange, secondDateRange)
        ) {
          for (let table of curr.tables)
            prev.push({
              tableId: table.id,
              startDate: curr.startDate,
              status: TableStatuses.TWO_HOURS,
              parkingNeeded: curr.vehiclesNumber > 0 && !curr.parkingCodeDelivered,
            });
        }

        return prev;
      }, [] as TableAvailability[]);
    this.notAvailableTables = parkingNeededTables;
    this.notAvailableTablesSub.next(this.notAvailableTables);
    this.unselectAllTables();

    return parkingNeededTables;
  }

  calculateNotAvailableTables(
    reservationTables: ReservationTables[],
    startDate: Date
  ): TableAvailability[] {
    let notAvailableTables: TableAvailability[] = [];
    // Okna rozważanej rezerwacji wydłużone o bufor po jej zakończeniu.
    let twoHoursDateRange: DWDateRange = {
      start: startDate,
      end: addMinutes(addHours(startDate, 2), RESERVATION_GAP_MINUTES),
    };

    let oneHourHalfDateRange: DWDateRange = {
      start: startDate,
      end: addMinutes(startDate, 90 + RESERVATION_GAP_MINUTES),
    };

    let occupiedDateRange: DWDateRange = {
      start: startDate,
      end: startDate,
    };

    notAvailableTables = reservationTables
      .filter(
        (reservationTable) =>
          reservationTable.status === ReservationStatus.CREATED ||
          reservationTable.status === ReservationStatus.GUEST_ARRIVED
      )
      .reduce((prev, curr) => {
        // Bufor przerwy między rezerwacjami tego samego stolika.
        let secondDateRange: DWDateRange = {
          start: curr.startDate,
          end: addMinutes(curr.endDate, RESERVATION_GAP_MINUTES),
        };
        if (
          this.checkIfDateRangesIntersects(occupiedDateRange, secondDateRange)
        ) {
          for (let tableId of curr.reservedTables)
            prev.push({
              tableId,
              startDate: curr.startDate,
              status: TableStatuses.OCCUPIED,
              parkingNeeded: false,
            });
        } else if (
          this.checkIfDateRangesIntersects(oneHourHalfDateRange, secondDateRange)
        ) {
          for (let tableId of curr.reservedTables)
            prev.push({
              tableId,
              startDate: curr.startDate,
              status: TableStatuses.ONE_HOUR_HALF,
              parkingNeeded: false,
            });
        } else if (
          this.checkIfDateRangesIntersects(twoHoursDateRange, secondDateRange)
        ) {
          for (let tableId of curr.reservedTables)
            prev.push({
              tableId,
              startDate: curr.startDate,
              status: TableStatuses.TWO_HOURS,
              parkingNeeded: false,
            });
        }

        return prev;
      }, [] as TableAvailability[]);
    this.notAvailableTables = notAvailableTables;
    this.notAvailableTablesSub.next(this.notAvailableTables);
    this.unselectAllTables();
    return notAvailableTables;
  }

  checkIfDateRangesIntersects(
    firstDateRange: DWDateRange,
    secondDate: DWDateRange
  ): boolean {
    return (
      (isBefore(firstDateRange.start, secondDate.start) &&
        isAfter(firstDateRange.end, secondDate.start)) ||
      (!isBefore(firstDateRange.start, secondDate.start) &&
        isBefore(firstDateRange.start, secondDate.end))
    );
  }

  sendReservationRequest(
    reservationRequest: ReservationWithTablesRequest
  ): Observable<DwResponse> {
    return this.http.post<DwResponse>(this.reservationsUrl, reservationRequest);
  }

  getReservationTables(reservationDate: Date): Observable<ReservationTables[]> {
    return this.http
      .get<ReservationTables[]>(
        this.reservationTablesUrl +
        `?reservation_date=${reservationDate.toISOString()}`
      )
      .pipe(
        map((reservationTables) =>
          reservationTables.map((reservationTable) => {
            return {
              id: reservationTable.id,
              startDate: new Date(reservationTable.startDate),
              endDate: new Date(reservationTable.endDate),
              reservedTables: reservationTable.reservedTables,
              status: reservationTable.status,
            };
          })
        )
      );
  }

  getTables(): Observable<Table[]> {
    return this.http.get<Table[]>(this.tablesUrl).pipe(
      map((tables) =>
        tables
          .filter((table) => table.visible)
          .sort((a, b) => (a.tableNumber > b.tableNumber ? 1 : -1))
      )
    );
  }

  prepareHoursOptions(
    day: Date,
    openingHours: OpeningHour[],
    compareDate: Date,
    minTimeBeforeEnd: number = 120
  ): MatSelectOption<Date>[] {
    let dateOptions: MatSelectOption<Date>[] = [];
    let startDates: Date[] = [];
    const openingHour: OpeningHour = openingHours.find(
      (openingHour) => openingHour.order === getISODayInWarsaw(day)
    )!;
    let i = openingHour.startOfOpening;
    do {
      const startDate = toWarsawSlot(day, i);

      if (isBefore(compareDate, startDate)) startDates.push(startDate);
      i += 30;
    } while (
      i <= openingHour.startOfOpening + openingHour.timeOpen - minTimeBeforeEnd
      );
    for (let date of startDates) {
      dateOptions.push({ label: formatInTimeZone(date, RESTAURANT_TIMEZONE, 'HH:mm'), value: date });
    }
    return dateOptions;
  }

  prepareDaysOptions(
    openingHours: OpeningHour[],
    specialDays: SpecialDay[] = []
  ): MatSelectOption<Date>[] {
    const today = toWarsawSlot(new Date(), 0);
    const endDate = startOfDay(addMonths(today, 6));
    let dateOptions: MatSelectOption<Date>[] = [];

    for (
      let date = today, idx = 0;
      isBefore(date, endDate) || isSameDay(date, endDate);
      date = addDays(date, 1)
    ) {
      const specDay = specialDays.find((day) =>
        isSameDayInWarsaw(new Date(day.specialDate), date)
      );

      if (specDay) {
        if (specDay.isOpen) {
          dateOptions.push({ label: getDayLabel(date, idx), value: date });
          idx++;
        }
      } else if (
        openingHours.find((openingHour) => openingHour.order === getISODayInWarsaw(date))
          ?.isOpen
      ) {
        dateOptions.push({ label: getDayLabel(date, idx), value: date });
        idx++;
      }
    }

    return dateOptions;

    function getDayLabel(date: Date, idx: number): string {
      const ddMm = formatInTimeZone(date, RESTAURANT_TIMEZONE, 'dd.MM');
      switch (idx) {
        case 0: {
          return 'DZIŚ ' + ddMm;
        }
        case 1: {
          return 'JUTRO ' + ddMm;
        }
        case 2: {
          return 'POJUTRZE ' + ddMm;
        }
        default: {
          return ddMm;
        }
      }
    }
  }

  prepareHoursOptionsForSpecialDay(
    specialDay: SpecialDay | undefined,
    compareDate: Date,
    minTimeBeforeEnd: number = 120
  ): MatSelectOption<Date>[] {
    if (specialDay) {
      let dateOptions: MatSelectOption<Date>[] = [];
      let startDates: Date[] = [];
      let i = specialDay.startOfOpen;
      do {
        const startDate = toWarsawSlot(
          new Date(specialDay.specialDate),
          i
        );
        if (isBefore(compareDate, startDate)) {
          startDates.push(startDate);
        }
        i += 30;
      } while (
        i <= specialDay.startOfOpen + specialDay.timeOpen - minTimeBeforeEnd
        );
      for (let date of startDates) {
        dateOptions.push({ label: formatInTimeZone(date, RESTAURANT_TIMEZONE, 'HH:mm'), value: date });
      }
      return dateOptions;
    }
    return [];
  }
}
